// ChangeRequirements.jsx - UPDATED with reviewer and shaId fields and auto-load on edit
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Container, Paper, Typography, Grid, Button, Tooltip, Snackbar, Alert, Tabs, Tab, CircularProgress, FormControl, InputLabel, Select, MenuItem, Chip } from '@mui/material';
import { AlertTriangle as AlertTriangleIcon } from 'lucide-react';

// Import separated components
import IssueFormFields from './issueformfield.jsx';
import IssueSidebar from './IssuedataforChnageRequirement.jsx';
import FileUploadSection from './fileupload.jsx';
import HierarchySection from './hierarchy.jsx';
import { HierarchySelectionDialog, NewLabelDialog } from './dialougecomponent.jsx';

// Import hooks and utilities
import { useChangeForm } from './useissueformCR.jsx';
import { issueTypes } from './issuetype.jsx';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function ChangeRequirementsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const urlProjectId = params.id;

  const isEditMode = location.state?.editMode || false;
  const issueToEdit = location.state?.issueData || null;

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [localUploadedFiles, setLocalUploadedFiles] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);

  // States for parent selection editing
  const [parentType, setParentType] = useState('');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [isParentLoading, setIsParentLoading] = useState(false);
  const [editIssueId, setEditIssueId] = useState(null);
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [availableParents, setAvailableParents] = useState([]);
  const [isAutoLoading, setIsAutoLoading] = useState(false);

  const initialIssueData = {
    id: '',
    project_id: '',
    issueType: 5,
    summary: '',
    description: '',
    assignee: '',
    reviewer: '',
    productOwner: '',
    productOwnerName: '',
    priority: 3,
    severity: 3,
    status: 1,
    bugStatus: 1,
    version: '',
    fixedInVersion: '',
    shaId: '',
    labels: [],
    startDate: '',
    endDate: '',
    timeEstimate: '',
    attachments: [],
    epic_id: '',
    story_id: '',
    task_id: '',
    linkedRequirementId: '',
    watcher: ''
  };

  const {
    issueData,
    setIssueData,
    projects,
    teamMembers,
    labels,
    setLabels,
    selectedHierarchy,
    setSelectedHierarchy,
    projectHierarchy,
    epics,
    stories,
    tasks,
    uploadedFiles,
    setUploadedFiles,
    attachedFiles,
    setAttachedFiles,
    hierarchicalFiles,
    setHierarchicalFiles,
    groupedFiles,
    setGroupedFiles,
    errors,
    snackbar,
    openLabelDialog,
    setOpenLabelDialog,
    showHierarchyDialog,
    setShowHierarchyDialog,
    newLabel,
    setNewLabel,
    labelError,
    setLabelError,
    requirements,
    isRequirementsLoading,
    handleRequirementSelect,
    isLoading,
    isTeamMembersLoading,
    isProductOwnerLoading,
    isLabelLoading,
    setIsLabelLoading,
    isFileUploading,
    setIsFileUploading,
    isEpicsLoading,
    isStoriesLoading,
    isTasksLoading,
    handleChange,
    handleProjectChange,
    handleEpicChange,
    handleStoryChange,
    handleTaskChange,
    handleLabelsChange,
    handleSubmit,
    handleCloseSnackbar,
    showSnackbar,
    handleFileUpload,
    handleFileRemove,
    createUploadPath,
    validateForm,
    ensureArray,
    apiClient
  } = useChangeForm();

  // Initialize project from URL/state/localStorage
  useEffect(() => {
    let projectIdToUse = null;
    let projectNameToUse = '';

    if (urlProjectId) {
      projectIdToUse = urlProjectId;
      const savedName = localStorage.getItem(`projectName_${urlProjectId}`) || localStorage.getItem('selectedProjectName');
      projectNameToUse = savedName || `Project ${urlProjectId}`;
    } else if (location.state?.selectedProjectId) {
      projectIdToUse = location.state.selectedProjectId;
      projectNameToUse = location.state.selectedProjectName || '';
    } else {
      const savedProjectId = localStorage.getItem('selectedProject');
      const savedProjectName = localStorage.getItem('selectedProjectName');
      if (savedProjectId) {
        projectIdToUse = savedProjectId;
        projectNameToUse = savedProjectName || '';
      }
    }

    if (projectIdToUse) {
      setSelectedProjectId(projectIdToUse);
      setSelectedProjectName(projectNameToUse);
      localStorage.setItem('selectedProject', projectIdToUse);
      localStorage.setItem('selectedProjectName', projectNameToUse);
      localStorage.setItem(`projectName_${projectIdToUse}`, projectNameToUse);
    }

    setIsInitializing(false);
  }, [location.state, urlProjectId]);

  // Helper to ensure attachments is always an array
  const ensureAttachmentsArray = (attachments) => {
    if (Array.isArray(attachments)) {
      return attachments;
    } else if (attachments === null || attachments === undefined) {
      return [];
    } else if (typeof attachments === 'string') {
      try {
        const parsed = JSON.parse(attachments);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    } else {
      return [];
    }
  };

useEffect(() => {
  const loadEditData = async () => {
    if (isEditMode && issueToEdit) {
      setIsAutoLoading(true);
      
      setEditIssueId(issueToEdit.id);
      
      try {
        // Fetch complete issue data using getIssueById
        const fullIssueResponse = await apiClient.getIssueById(issueToEdit.id);
        
        if (!fullIssueResponse || !fullIssueResponse.data) {
          throw new Error('Failed to fetch issue data');
        }
        
        const fullIssue = fullIssueResponse.data;
        
        // Parse labels properly
        let parsedLabels = [];
        if (fullIssue.labels) {
          if (Array.isArray(fullIssue.labels)) {
            parsedLabels = fullIssue.labels;
          } else if (typeof fullIssue.labels === 'string') {
            parsedLabels = fullIssue.labels.split(',').map(l => l.trim()).filter(Boolean);
          }
        }

        // Parse watcher
        let parsedWatcher = '';
        if (fullIssue.watcher) {
          parsedWatcher = fullIssue.watcher;
        } else if (fullIssue.watchers) {
          if (Array.isArray(fullIssue.watchers) && fullIssue.watchers.length > 0) {
            parsedWatcher = fullIssue.watchers[0];
          } else if (typeof fullIssue.watchers === 'string') {
            try {
              const watchersArray = JSON.parse(fullIssue.watchers);
              parsedWatcher = watchersArray[0] || '';
            } catch {
              parsedWatcher = '';
            }
          }
        }

        // Parse reviewer
        let parsedReviewer = fullIssue.reviewer || '';

        // Parse shaId - handle both string and object formats
        let parsedShaId = '';
        if (fullIssue.shaId) {
          if (typeof fullIssue.shaId === 'object' && fullIssue.shaId !== null) {
            parsedShaId = fullIssue.shaId.id || fullIssue.shaId.shaId || '';
          } else if (typeof fullIssue.shaId === 'string') {
            parsedShaId = fullIssue.shaId;
          }
        } else if (fullIssue.shaIds && Array.isArray(fullIssue.shaIds) && fullIssue.shaIds.length > 0) {
          const mostRecentSha = fullIssue.shaIds[fullIssue.shaIds.length - 1];
          if (typeof mostRecentSha === 'object' && mostRecentSha !== null) {
            parsedShaId = mostRecentSha.id || mostRecentSha.shaId || '';
          } else if (typeof mostRecentSha === 'string') {
            parsedShaId = mostRecentSha;
          }
        }

        // Ensure attachments array
        const parsedAttachments = ensureAttachmentsArray(fullIssue.attachments);

        // Format dates
        const formatDate = (dateStr) => {
          if (!dateStr) return '';
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        };

        // Set basic issue data first
        setIssueData({
          ...initialIssueData,
          id: fullIssue.id || '',
          project_id: fullIssue.project_id || fullIssue.project || '',
          issueType: fullIssue.issue_type || fullIssue.issueType || 5,
          summary: fullIssue.summary || '',
          description: fullIssue.description || '',
          assignee: fullIssue.assignee_id || fullIssue.assignee || '',
          reviewer: parsedReviewer,
          reporter: fullIssue.reporter || '',
          lead: fullIssue.lead || '',
          productOwner: fullIssue.productOwner || fullIssue.productOwner || '',
          productOwnerName: fullIssue.productOwnerName || fullIssue.productOwnerName || '',
          priority: fullIssue.priority || 3,
          severity: fullIssue.severity || 3,
          status: fullIssue.status || 1,
          bugStatus: fullIssue.bug_status || fullIssue.bugStatus || 1,
          version: fullIssue.version || '',
          foundInBuild: fullIssue.foundInBuild || fullIssue.found_in_build || '',
          fixedInBuild: fullIssue.fixedInBuild || fullIssue.fixed_in_build || '',
          fixedInVersion: fullIssue.fixedInVersion || fullIssue.fixed_in_version || '',
          shaId: parsedShaId,
          labels: parsedLabels,
          startDate: formatDate(fullIssue.startDate || fullIssue.start_date),
          endDate: formatDate(fullIssue.endDate || fullIssue.end_date),
          timeEstimate: fullIssue.time_estimate || fullIssue.timeEstimate || '',
          attachments: parsedAttachments,
          epic_id: fullIssue.epic_id || '',
          story_id: fullIssue.story_id || '',
          task_id: fullIssue.task_id || '',
          linkedRequirementId: fullIssue.linked_requirement_id || fullIssue.linkedRequirementId || '',
          watcher: parsedWatcher
        });

        // Load project data
        const projectId = fullIssue.project_id || fullIssue.project;
        if (projectId) {
          
          // First set the project and load project data
          await handleProjectChange({ target: { value: projectId } });
          
          // Then load hierarchy data with delays to ensure proper loading
          if (fullIssue.epic_id) {
            setTimeout(async () => {
              await handleEpicChange({ target: { value: fullIssue.epic_id } });
            }, 500);
          }
          
          if (fullIssue.story_id) {
            setTimeout(async () => {
              await handleStoryChange({ target: { value: fullIssue.story_id } });
            }, 800);
          }
          
          if (fullIssue.task_id) {
            setTimeout(async () => {
              await handleTaskChange({ target: { value: fullIssue.task_id } });
            }, 1100);
          }
        }

        showSnackbar(`Loaded data for editing: ${fullIssue.summary}`, 'success');
        
      } catch (error) {
        showSnackbar('Failed to load issue data for editing', 'error');
      } finally {
        setIsAutoLoading(false);
      }
    }
  };

  loadEditData();
}, [isEditMode, issueToEdit]);
  // Redirect if no project
  useEffect(() => {
    if (!isInitializing && !selectedProjectId) {
      navigate('/active-projects');
    }
  }, [isInitializing, selectedProjectId, navigate]);

  // Auto-set project_id
  useEffect(() => {
    if (selectedProjectId && !issueData.project_id && !isEditMode && !editIssueId) {
      setIssueData({
        ...initialIssueData,
        project_id: selectedProjectId,
        projectName: selectedProjectName,
        attachments: [],
        reviewer: '',
        shaId: ''
      });
      if (handleProjectChange) {
        handleProjectChange({
          target: { name: 'project_id', value: selectedProjectId }
        });
      }
     
    }
  }, [selectedProjectId, selectedProjectName, issueData.project_id, isEditMode, editIssueId, setIssueData, handleProjectChange, showSnackbar]);

  useEffect(() => {
    if (uploadedFiles && Array.isArray(uploadedFiles)) {
      setLocalUploadedFiles(uploadedFiles);
    }
  }, [uploadedFiles]);

  useEffect(() => {
    setCurrentTab(location.pathname.includes('CR') ? 1 : 0);
  }, [location.pathname]);

  const selectedIssueType = issueData?.issueType ? 
    issueTypes.find(type => type.id === parseInt(issueData.issueType)) : null;

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (newValue === 0 && selectedProjectId) {
      navigate(`/project/${selectedProjectId}/create-task`);
    } else if (newValue === 1 && selectedProjectId) {
      navigate(`/project/${selectedProjectId}/CR`);
    }
  };

  // FIXED: Handle parent type change
  const handleParentTypeChange = async (event) => {
    const type = event.target.value;
    
    setParentType(type);
    setSelectedParentId('');
    setIsParentLoading(true);
    setAvailableParents([]);

    if (type && selectedProjectId) {
      try {
        let parents = [];
        let issueTypeId;
        switch (type) {
          case 'epic': issueTypeId = 2; break;
          case 'story': issueTypeId = 3; break;
          case 'task': issueTypeId = 4; break;
          case 'bug': issueTypeId = 1; break;
          default: issueTypeId = null;
        }

        if (issueTypeId) {
          
          const allIssues = await apiClient.getIssues({ 
            project_id: selectedProjectId, 
            issue_type: issueTypeId 
          });
          
          let rawData = [];
          if (allIssues && allIssues.data) {
            rawData = allIssues.data;
          } else if (Array.isArray(allIssues)) {
            rawData = allIssues;
          }

          // Filter to ensure only exact type
          const filteredData = rawData.filter(issue => {
            const issueType = parseInt(issue.issue_type || issue.issueType || 0);
            return issueType === issueTypeId;
          });

          parents = filteredData.map(issue => ({
            id: issue.id,
            label: issue.summary || issue.name || `Item ${issue.id}`
          }));

        }

        setAvailableParents(parents);
        
      } catch (error) {
        showSnackbar('Failed to load parent items', 'error');
      }
    }
    setIsParentLoading(false);
  };

  // Helper to get company_id from token
  const getCompanyId = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found. Please log in.");
    }
    
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      throw new Error('Invalid token format');
    }
    
    try {
      const payload = JSON.parse(atob(tokenParts[1]));
      return payload.company_id;
    } catch (err) {
      throw new Error('Failed to parse token payload');
    }
  };

  // FIXED: Handle parent select with complete data loading
  const handleParentSelect = async (event) => {
    const parentId = event.target.value;
    
    setSelectedParentId(parentId);
    setIsParentLoading(true);
    setEditIssueId(parentId);

    if (parentId && selectedProjectId) {
      try {
        
        const fullIssue = await apiClient.getIssueById(parentId);
        
        if (fullIssue && fullIssue.data) {
          const issueDataToUse = fullIssue.data;
          
          // Get SHA ID
          let parsedShaId = '';
          if (issueDataToUse.shaId) {
            if (typeof issueDataToUse.shaId === 'object' && issueDataToUse.shaId !== null) {
              parsedShaId = issueDataToUse.shaId.id || issueDataToUse.shaId.shaId || '';
            } else if (typeof issueDataToUse.shaId === 'string') {
              parsedShaId = issueDataToUse.shaId;
            }
          } else if (issueDataToUse.shaIds && Array.isArray(issueDataToUse.shaIds) && issueDataToUse.shaIds.length > 0) {
            const mostRecentSha = issueDataToUse.shaIds[issueDataToUse.shaIds.length - 1];
            if (typeof mostRecentSha === 'object' && mostRecentSha !== null) {
              parsedShaId = mostRecentSha.id || mostRecentSha.shaId || '';
            } else if (typeof mostRecentSha === 'string') {
              parsedShaId = mostRecentSha;
            }
          }
          
          // Parse labels
          let parsedLabels = [];
          if (issueDataToUse.labels) {
            if (Array.isArray(issueDataToUse.labels)) {
              parsedLabels = issueDataToUse.labels;
            } else if (typeof issueDataToUse.labels === 'string') {
              parsedLabels = issueDataToUse.labels.split(',').map(l => l.trim()).filter(Boolean);
            }
          }

          let parsedWatcher = issueDataToUse.watcher || '';
          let parsedReviewer = issueDataToUse.reviewer || '';
          
          const parsedAttachments = ensureAttachmentsArray(issueDataToUse.attachments);

          const formatDate = (dateStr) => {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
          };

          // Update issue data with all fields
          setIssueData({
            ...initialIssueData,
            id: issueDataToUse.id || '',
            project_id: issueDataToUse.project_id ||issueDataToUse.project|| selectedProjectId,
            issueType: issueDataToUse.issue_type || issueDataToUse.issueType || '',
            summary: issueDataToUse.summary || '',
            description: issueDataToUse.description || '',
            assignee: issueDataToUse.assignee_id || issueDataToUse.assignee || '',
            reviewer: parsedReviewer,
            reporter:issueDataToUse.reporter || '',
            productOwner: issueDataToUse.product_owner_id || issueDataToUse.productOwner || '',
            productOwnerName: issueDataToUse.product_owner_name || issueDataToUse.productOwnerName || '',
            priority: issueDataToUse.priority || 3,
            severity: issueDataToUse.severity || 3,
            status: issueDataToUse.status || 1,
            bugStatus: issueDataToUse.bug_status || issueDataToUse.bugStatus || 1,
            version: issueDataToUse.version || '',
            lead:issueDataToUse.lead || '',
            fixedInBuild:issueDataToUse.fixedInBuild||'',
            foundInBuild:issueDataToUse.foundInBuild||'',
            fixedInVersion: issueDataToUse.fixed_in_version || issueDataToUse.fixedInVersion || '',
            shaId: parsedShaId,
            labels: parsedLabels,
            startDate: formatDate(issueDataToUse.start_date || issueDataToUse.startDate),
            endDate: formatDate(issueDataToUse.end_date || issueDataToUse.endDate),
            timeEstimate: issueDataToUse.time_estimate || issueDataToUse.timeEstimate || '',
            attachments: parsedAttachments,
            epic_id: issueDataToUse.epic_id || '',
            story_id: issueDataToUse.story_id || '',
            task_id: issueDataToUse.task_id || '',
            linkedRequirementId: issueDataToUse.linked_requirement_id || issueDataToUse.linkedRequirementId || '',
            watcher: parsedWatcher
          });
          // Load hierarchy data
          if (issueDataToUse.project_id) {
            await handleProjectChange({ target: { value: issueDataToUse.project_id } });
            
            if (issueDataToUse.epic_id) {
              setTimeout(() => {
                handleEpicChange({ target: { value: issueDataToUse.epic_id } });
              }, 200);
            }
            if (issueDataToUse.story_id) {
              setTimeout(() => {
                handleStoryChange({ target: { value: issueDataToUse.story_id } });
              }, 300);
            }
            if (issueDataToUse.task_id) {
              setTimeout(() => {
                handleTaskChange({ target: { value: issueDataToUse.task_id } });
              }, 400);
            }
          }

          showSnackbar(`Loaded data for editing: ${issueDataToUse.summary}`, 'success');
        }
      } catch (error) {
        showSnackbar('Failed to load parent data', 'error');
      }
    }
    setIsParentLoading(false);
  };

  // Local submit handler
  const handleLocalSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      showSnackbar('Please fix the errors before submitting.', 'error');
      return;
    }
    
    setLocalIsLoading(true);
    
    try {
      const formatDateForSubmit = (dateStr) => dateStr || null;

      const submissionData = {
        ...issueData,
        epic_id: issueData.epic_id || null,
        story_id: issueData.story_id || null, 
        task_id: issueData.task_id || null,
        linkedRequirementId: issueData.linkedRequirementId || null,
        reviewer: issueData.reviewer || null,
        shaId: issueData.shaId || null,
        labels: Array.isArray(issueData.labels) ? issueData.labels.join(',') : issueData.labels,
        startDate: formatDateForSubmit(issueData.startDate),
        endDate: formatDateForSubmit(issueData.endDate),
        priority: parseInt(issueData.priority),
        severity: parseInt(issueData.severity),
        status: parseInt(issueData.status),
        bugStatus: parseInt(issueData.bugStatus),
        project_id: parseInt(issueData.project_id),
        issueType: parseInt(issueData.issueType),
        watcher: issueData.watcher || null,
        attachments: Array.isArray(issueData.attachments) ? issueData.attachments : []
      };
      
      if (editIssueId) {
        await apiClient.updateIssue(editIssueId, submissionData, attachedFiles);
        showSnackbar('Item updated successfully!', 'success');
      } else {
        await apiClient.createIssue(submissionData, attachedFiles);
        showSnackbar('Change Requirement created successfully!', 'success');
      }
      
      if (!editIssueId) {
        setIssueData(initialIssueData);
        setParentType('');
        setSelectedParentId('');
        setAvailableParents([]);
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to submit. Check console for details.', 'error');
    } finally {
      setLocalIsLoading(false);
    }
  };

  const effectiveIsLoading = isLoading || localIsLoading || isAutoLoading;

  if (isInitializing) {
    return (
      <Container maxWidth="1500px" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Initializing Change Requirements...</Typography>
      </Container>
    );
  }

  if (!selectedProjectId) {
    return (
      <Container maxWidth="1500px" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            No project selected. Please select a project from Active Projects.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/active-projects')} sx={{ mt: 2 }}>
            Go to Active Projects
          </Button>
        </Paper>
      </Container>
    );
  }

  const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || `HTTP ${response.status}: ${response.statusText}`;
        } catch {
          errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
        }
        
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return await response.text();
    } catch (error) {
      throw error;
    }
  };

  const handleOpenLabelDialog = () => {
    if (typeof setOpenLabelDialog === 'function') {
      setOpenLabelDialog(true);
    }
  };

  const handleCloseLabelDialog = () => {
    if (typeof setOpenLabelDialog === 'function') {
      setOpenLabelDialog(false);
    }
    if (typeof setNewLabel === 'function') {
      setNewLabel({ name: '', color: '#0052cc' });
    }
    if (typeof setLabelError === 'function') {
      setLabelError('');
    }
  };

  const handleNewLabelChange = (event) => {
    const { name, value } = event.target;
    if (typeof setNewLabel === 'function') {
      setNewLabel(prev => ({
        ...prev,
        [name]: value
      }));
    }
    if (typeof setLabelError === 'function') {
      setLabelError('');
    }
  };

  const handleCreateLabel = async () => {
    if (!newLabel?.name?.trim()) {
      if (typeof setLabelError === 'function') {
        setLabelError('Label name is required');
      }
      return;
    }
  
    try {
      if (typeof setIsLabelLoading === 'function') {
        setIsLabelLoading(true);
      }
  
      let newLabelData;
      try {
        const token = localStorage.getItem('token');
       
  
        const result = await apiCall('/api/labels', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: newLabel.name.trim(),
            color: newLabel.color || '#0052cc'
          }),
        });
  
        newLabelData = result;
      } catch (error) {
        console.warn('Server label creation failed, creating locally:', error.message);
        newLabelData = {
          id: Date.now(),
          name: newLabel.name.trim(),
          color: newLabel.color || '#0052cc'
        };
      }
  
      if (typeof setLabels === 'function') {
        setLabels(prev => [...(prev || []), newLabelData]);
      }
  
      handleCloseLabelDialog();
      if (typeof showSnackbar === 'function') {
        showSnackbar('Label created successfully!', 'success');
      }
    } catch (error) {
      if (typeof setLabelError === 'function') {
        setLabelError('Failed to create label');
      }
      if (typeof showSnackbar === 'function') {
        showSnackbar('Failed to create label', 'error');
      }
    } finally {
      if (typeof setIsLabelLoading === 'function') {
        setIsLabelLoading(false);
      }
    }
  };

  const handleHierarchicalFileUpload = (files) => {
    handleFileUpload(files, {
      selectedHierarchy,
      issueType: issueData.issueType,
      issueId: issueData.id
    });
  };

  return (
    <Box className="flex-grow">
      <Snackbar
        open={snackbar?.open || false}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar}
          severity={snackbar?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {snackbar?.message || ''}
        </Alert>
      </Snackbar>

      <HierarchySelectionDialog
        open={showHierarchyDialog || false}
        onClose={() => setShowHierarchyDialog && setShowHierarchyDialog(false)}
        selectedHierarchy={selectedHierarchy}
        createUploadPath={createUploadPath || (() => '')}
        onFileUpload={handleHierarchicalFileUpload}
        issueData={issueData}
      />
      
      <NewLabelDialog
        open={openLabelDialog || false}
        onClose={handleCloseLabelDialog}
        newLabel={newLabel || { name: '', color: '#0052cc' }}
        onChange={handleNewLabelChange}
        error={labelError || ''}
        isLoading={isLabelLoading || false}
        onCreate={handleCreateLabel}
      />

      <Container maxWidth="1500px" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, border: '2px solid', borderColor: 'grey.300', boxShadow: 4 }}>
          <Typography variant="h4" component="h1" sx={{  fontWeight: 'bold', mb: 4 }}>
            Change Requirements for {selectedProjectName}
          </Typography>
          
    

          {/* Parent Selection Section */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 1, bgcolor: 'background.default' }}>
            <Typography variant="h6" gutterBottom>Existing Item for Editing </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Select Item Type</InputLabel>
                  <Select
                    value={parentType}
                    label="Select Item Type"
                    onChange={handleParentTypeChange}
                    disabled={isAutoLoading}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="epic">Epic</MenuItem>
                    <MenuItem value="story">Story</MenuItem>
                    <MenuItem value="task">Task</MenuItem>
                    <MenuItem value="bug">Bug</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                {parentType && (
                  <FormControl fullWidth disabled={isParentLoading || isAutoLoading}>
                    <InputLabel>Select {parentType.charAt(0).toUpperCase() + parentType.slice(1)}</InputLabel>
                    <Select
                      value={selectedParentId}
                      label={`Select ${parentType}`}
                      onChange={handleParentSelect}
                    >
                      <MenuItem value="">
                        <em>Select an item...</em>
                      </MenuItem>
                      {availableParents.length > 0 ? (
                        availableParents.map(parent => (
                          <MenuItem key={parent.id} value={parent.id}>
                            {parent.label}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No {parentType}s found</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setParentType('');
                    setSelectedParentId('');
                    setEditIssueId(null);
                    setAvailableParents([]);
                    setIssueData({
                      ...initialIssueData,
                      attachments: [],
                      reviewer: '',
                      shaId: ''
                    });
                  }}
                  fullWidth
                  disabled={isAutoLoading}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
            {editIssueId && !isAutoLoading && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Editing {parentType || 'item'} ID: {editIssueId}. All fields loaded.
                </Typography>
              </Alert>
            )}
            {(isParentLoading || isAutoLoading) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>
                  {isAutoLoading ? 'Auto-loading issue data...' : 'Loading type-specific data...'}
                </Typography>
              </Box>
            )}
          </Paper>
          
          <form onSubmit={handleLocalSubmit}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  <IssueFormFields
                    issueData={issueData || initialIssueData}
                    errors={errors || {}}
                    projects={projects || []}
                    epics={epics || []}
                    stories={stories || []}
                    tasks={tasks || []}
                    isLoading={effectiveIsLoading}
                    isEditMode={!!editIssueId || isEditMode}
                    isEpicsLoading={isEpicsLoading || false}
                    isStoriesLoading={isStoriesLoading || false}
                    isTasksLoading={isTasksLoading || false}
                    handleChange={handleChange}
                    handleProjectChange={handleProjectChange}
                    handleEpicChange={handleEpicChange}
                    handleStoryChange={handleStoryChange}
                    handleTaskChange={handleTaskChange}
                    selectedProjectName={selectedProjectName}
                    teamMembers={teamMembers || []}
                    requirements={requirements || []}
                    isRequirementsLoading={isRequirementsLoading || false}
                    handleRequirementSelect={handleRequirementSelect}
                  />

                  <HierarchySection
                    selectedHierarchy={selectedHierarchy}
                    issueData={issueData || initialIssueData}
                    epics={epics || []}
                    stories={stories || []}
                    tasks={tasks || []}
                    isEpicsLoading={isEpicsLoading || false}
                    isStoriesLoading={isStoriesLoading || false}
                    isTasksLoading={isTasksLoading || false}
                    handleEpicChange={handleEpicChange}
                    handleStoryChange={handleStoryChange}
                    handleTaskChange={handleTaskChange}
                    createUploadPath={createUploadPath || (() => '')}
                  />

                  <FileUploadSection
                    uploadedFiles={uploadedFiles}
                    isFileUploading={isFileUploading || false}
                    createUploadPath={createUploadPath || (() => '')}
                    handleFileUpload={handleFileUpload}
                    handleRemoveFile={handleFileRemove}
                    setShowHierarchyDialog={setShowHierarchyDialog}
                    selectedHierarchy={selectedHierarchy}
                    issueType={issueData?.issueType}
                    issueId={issueData?.id || editIssueId}
                    setIsFileUploading={setIsFileUploading}
                  />
                </Grid>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <IssueSidebar
                  issueData={issueData || initialIssueData}
                  selectedIssueType={selectedIssueType}
                  teamMembers={teamMembers || []}
                  labels={labels || []}
                  errors={errors || {}}
                  isLoading={effectiveIsLoading}
                  isTeamMembersLoading={isTeamMembersLoading || false}
                  isProductOwnerLoading={isProductOwnerLoading || false}
                  handleChange={handleChange}
                  handleLabelsChange={handleLabelsChange}
                  handleOpenLabelDialog={handleOpenLabelDialog}
                  currentTab={currentTab}
                  onTabChange={handleTabChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    disabled={effectiveIsLoading}
                  >
                    Cancel
                  </Button>
                  
                  <Tooltip title={effectiveIsLoading ? 'Processing...' : ''}>
                    <span>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={effectiveIsLoading}
                        startIcon={effectiveIsLoading ? null : selectedIssueType?.icon || <AlertTriangleIcon />}
                      >
                        {effectiveIsLoading ? 'Processing...' : editIssueId ? 'Update' : 'Create Change Requirement'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}